import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Mail, X, ShieldAlert, Sparkles, CheckCircle2 } from "lucide-react";

export function AddTestAdminModal({ open, onClose, onSaved }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Google email is required");
            return;
        }
        setSaving(true);
        try {
            const res = await api.post("/admin/test-admins", {
                email: email.trim().toLowerCase(),
                name: name.trim() || undefined,
            });
            toast.success(`Admin access granted to ${res.data.email}!`);
            onSaved();
            onClose();
            setEmail("");
            setName("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add test admin");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div
                className="bg-white rounded-2xl border border-nest-line w-full max-w-md p-6 lg:p-8 shadow-2xl relative"
                onClick={(e) => e.stopPropagation()}
                data-testid="add-test-admin-modal"
            >
                <div className="flex items-center justify-between pb-4 border-b border-nest-line">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-xl bg-nest-ink text-white grid place-items-center">
                            <KeyRound size={18} />
                        </div>
                        <div>
                            <p className="nest-stat-label text-amber-700">Super Admin Access</p>
                            <h3 className="font-display text-2xl tracking-tight">Add Test Admin</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-nest-alt text-nest-ink3 hover:text-nest-ink">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-5 grid gap-4">
                    <div>
                        <label className="nest-stat-label">Google Account Email <span className="text-nest-clay">*</span></label>
                        <input
                            required
                            type="email"
                            placeholder="tester.colleague@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm font-mono text-xs"
                            data-testid="test-admin-email-input"
                        />
                        <p className="text-[11px] text-nest-ink3 mt-1">
                            The user will sign in using this Google account to access all Admin and Guard features.
                        </p>
                    </div>

                    <div>
                        <label className="nest-stat-label">Tester Name / Note (Optional)</label>
                        <input
                            type="text"
                            placeholder="e.g. John Doe (QA Testing)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm"
                            data-testid="test-admin-name-input"
                        />
                    </div>

                    <div className="pt-2 flex items-center justify-end gap-2 border-t border-nest-line mt-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl border border-nest-line text-sm hover:bg-nest-alt font-medium text-nest-ink"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="nest-btn-primary flex items-center gap-2 text-sm"
                            data-testid="submit-test-admin-btn"
                        >
                            {saving ? "Granting..." : "Grant Admin Access"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function TestAdminsManager() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [q, setQ] = useState("");

    const loadAdmins = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/test-admins");
            setAdmins(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load test admin accounts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAdmins();
    }, []);

    const handleRemoveAdmin = async (admin) => {
        if (!window.confirm(`Are you sure you want to revoke admin access for "${admin.name || admin.email}"? They will be logged out and removed.`)) {
            return;
        }
        try {
            await api.delete(`/admin/test-admins/${admin.user_id}`);
            toast.success(`Revoked admin access for ${admin.email}`);
            loadAdmins();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove test admin");
        }
    };

    const filtered = admins.filter((a) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return [a.name, a.email].some((v) => (v || "").toLowerCase().includes(s));
    });

    return (
        <div>
            {/* Owner Exclusive Notice Banner */}
            <div className="mb-6 p-4 rounded-2xl border border-amber-300 bg-amber-50/90 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="p-2 rounded-xl bg-amber-200 text-amber-900 shrink-0">
                        <KeyRound size={18} />
                    </span>
                    <div>
                        <div className="flex items-center gap-2">
                            <p className="font-semibold text-amber-950 text-sm">Super Admin Exclusive: Test Admin Management</p>
                            <span className="text-[9px] uppercase tracking-wider font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded">Super Admin Only</span>
                        </div>
                        <p className="text-xs text-amber-800 mt-0.5">
                            Exclusively available to <strong>aws.tilak007@gmail.com</strong>. Authorise colleagues to test admin/guard features, and revoke their access anytime when testing is finished. Test admins cannot add other admins.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6">
                <div>
                    <h2 className="font-display text-2xl tracking-tight">Active Test Admins</h2>
                    <p className="text-nest-ink2 text-sm mt-1">
                        Accounts authorized with full admin & testing switcher privileges ({admins.length} total).
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search test admins..."
                        className="bg-white border border-nest-line rounded-lg px-3.5 py-2 w-full sm:w-56 outline-none focus:border-nest-clay text-sm"
                        data-testid="test-admin-search-input"
                    />
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="nest-btn-primary flex items-center gap-2 whitespace-nowrap"
                        data-testid="open-add-test-admin-btn"
                    >
                        <Plus size={16} /> Add Test Admin
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-nest-ink2">Loading test accounts...</div>
            ) : filtered.length === 0 ? (
                <div className="nest-card p-10 text-center text-nest-ink2">
                    <div className="h-12 w-12 rounded-2xl bg-nest-alt mx-auto grid place-items-center text-nest-ink3 mb-3">
                        <KeyRound size={24} />
                    </div>
                    <p className="font-display text-lg text-nest-ink">No test admins currently active</p>
                    <p className="text-sm mt-1 max-w-md mx-auto">
                        Add a Google email address to immediately grant your colleague admin testing access on UAT.
                    </p>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="nest-btn-primary inline-flex items-center gap-2 mt-4"
                    >
                        <Plus size={16} /> Add First Test Admin
                    </button>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((a) => (
                        <div key={a.user_id} className="nest-card p-5 flex flex-col justify-between" data-testid={`test-admin-card-${a.user_id}`}>
                            <div>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        {a.picture ? (
                                            <img src={a.picture} alt="" className="h-12 w-12 rounded-full object-cover border border-nest-line" />
                                        ) : (
                                            <div className="h-12 w-12 rounded-xl bg-nest-ink text-white grid place-items-center font-display font-semibold text-lg">
                                                <KeyRound size={20} />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="font-semibold text-nest-ink truncate">{a.name || "Test Admin"}</p>
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 border border-amber-300">
                                                    Admin
                                                </span>
                                            </div>
                                            <p className="text-xs text-nest-ink3 font-mono truncate mt-0.5">{a.email}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveAdmin(a)}
                                        className="h-8 w-8 rounded-lg border border-nest-line hover:border-nest-err text-nest-ink3 hover:text-nest-err hover:bg-red-50/50 grid place-items-center transition cursor-pointer shrink-0"
                                        title="Revoke Admin Access"
                                        data-testid={`remove-test-admin-${a.user_id}`}
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>

                                <div className="mt-4 pt-3 border-t border-nest-line text-xs text-nest-ink3 space-y-1">
                                    <p className="flex items-center justify-between">
                                        <span>Status:</span>
                                        <span className="text-emerald-700 font-semibold flex items-center gap-1">
                                            <CheckCircle2 size={12} /> Active Test Admin
                                        </span>
                                    </p>
                                    <p className="flex items-center justify-between">
                                        <span>Role Switcher:</span>
                                        <span className="text-nest-ink font-medium">Enabled (Admin/Resident/Guard)</span>
                                    </p>
                                </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-nest-line">
                                <button
                                    onClick={() => handleRemoveAdmin(a)}
                                    className="w-full text-xs font-semibold py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition cursor-pointer text-center"
                                >
                                    Revoke Admin Privileges
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AddTestAdminModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSaved={loadAdmins}
            />
        </div>
    );
}
